<template>
    <div class="app-content-builder-editor content-builder-editor col-12 col-lg-9">
        <div v-if="currentDataObject"
            :key="currentDataObject.id"
            class="app-content-builder-editor__header--object app-content-builder-editor__header grid">
            <div class="app-content-builder-editor__header-actions col-12">
                <h3 class="app-content-builder-editor__header-title">
                    <span v-if="isShared">{{ getCleanTitle(currentDataObject) }} Editor</span>
                    <span v-else>{{ interaction ? interaction.name : currentDataObject.type }} Editor</span>
                </h3>
                <div class="app-content-builder-editor__revisions dropdown">
                    <button
                        type="button"
                        data-bs-toggle="dropdown"
                        @click="updateHistory(selectedId)">
                        <span class="element-chip is--info">
                            Rev {{ currentDataObject.revision }}
                            <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
                        </span>
                    </button>

                    <div class="app-dropdown dropdown-menu dropdown-menu-right">
                        <h6 class="app-dropdown__title">
                            Revision History
                        </h6>
                        <div v-show="loadingHistory" class="app-loader__wrapper">
                            <app-loader />
                        </div>
                        <div v-if="!currentHistory.length" class="app-dropdown__body">
                            <em>No history available.</em>
                        </div>
                        <button v-for="(revision, index) in currentHistory"
                            :key="`rev-${index}-${revision.id}`"
                            type="button"
                            :title="localizeDate(revision.updated_at).format('dddd, MMMM Do YYYY, h:mm:ss a')"
                            @click="selectRevision(revision)">
                            <span :class="['element-badge', currentDataObject.revision === revision.version ? 'badge-primary' : 'badge-light']">Rev {{ revision.version }}</span>
                            <p>
                                <em>{{ localizeDate(revision.updated_at).fromNow() }}</em>
                                <span v-if="revision.editor"> by {{ revision.editor.name }}</span>
                            </p>
                        </button>
                    </div>
                </div>
                <div v-if="!isLiteEditor" class="checkbox">
                    <input type="checkbox" class="checkbox__input" id="advanced-mode-toggle" v-model="advancedMode">
                    <label class="custom-control-label" for="advanced-mode-toggle">
                        <span>Advanced Editing</span>
                    </label>
                </div>
                <a v-if="canExportScript"
                    :href="exportUrl"
                    class="element-button--ghost element-button">
                    Export Script
                </a>
                <template v-if="!isShared">
                    <button v-if="!isLiteEditor" @click="duplicatePage(currentDataObject)"
                        class="element-button--icon element-button"
                        title="Duplicate Item">
                        <svg class="element-icon--copy element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-copy"></use></svg>
                    </button>
                    <button v-if="aiEnabled && !isLiteEditor"
                        @click="toggleAi()"
                        :disabled="currentObjectStatus === 'added' || currentObjectStatus === 'removed' || currentObjectStatus.data === ''"
                        class="element-button--ai-primary element-button"
                        title="Script with AI">
                        <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                        Script with AI
                    </button>
                    <template v-if="!isLiteEditor">
                    <button v-if="currentObjectStatus === 'removed'"
                        @click="restorePage(currentDataObject)"
                        class="element-button--secondary element-button is--success"
                        title="Restore Screen">
                        Restore
                    </button>
                    <button v-else
                        @click="removePage(currentDataObject)"
                        class="element-button--icon element-button is--alert"
                        title="Remove Screen">
                        <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                    </button>
                    </template>
                </template>
            </div>
            <!-- <div class="d-flex justify-content-between align-items-center">
                <div>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text p-2">Internal ID</span>
                        </div>
                        <div v-if="isShared"
                            :title="getDefaultName(currentDataObject)"
                            class="screen-name mr-2 p-2 border bg-white text-muted">
                            {{ currentDataObject.name }}
                        </div>
                        <div v-else
                            v-text="currentDataObject.name"
                            contenteditable="true"
                            spellcheck="false"
                            @blur="updatePageName"
                            :title="getDefaultName(currentDataObject)"
                            class="screen-name mr-2 p-2">
                        </div>
                    </div>
                </div>
            </div> -->
            <div v-if="!isShared" class="col-12 col-md-8">
                <div class="input text">
                    <label>Screen Title</label>
                    <div v-text="currentDataObject.title"
                        contenteditable="true"
                        spellcheck="false"
                        @blur="updatePageTitle"
                        class="input"
                        title="Screen Title">
                    </div>
                </div>
            </div>
            <div v-if="!isShared" class="col-12 col-md-4">
                <div class="input text">
                    <label>Internal ID</label>
                    <div v-text="currentDataObject.name"
                        contenteditable="true"
                        spellcheck="false"
                        @blur="updatePageName"
                        :title="getDefaultName(currentDataObject)"
                        class="input">
                    </div>
                </div>
            </div>
        </div>
        <div v-else
            class="app-content-builder-editor__preview-body-empty">
            <span v-if="outline.length" class="app-content-builder-editor__preview-body-empty-message element-empty-instructions">
                <img class="app-content-builder-editor__preview-body-empty-img" src="/img/undraw_select_option_re_u4qn.svg" />
                <p>Select a screen to edit.</p>
            </span>
            <span v-else class="app-content-builder-editor__preview-body-empty-message element-empty-instructions">
                <img class="app-content-builder-editor__preview-body-empty-img" src="/img/undraw_runner_start_x-0-uu.svg" />
                <p>Select "Add Screen" to get started!</p>
            </span>
        </div>
        <div v-show="loading && !advancedMode" class="app-loader is--fullscreen">
            <img src="/img/motif-progress-loader.svg"  alt="Loading....">
            <p>Loading...</p>
        </div>
        <div v-if="currentContent"
            class="app-content-builder-editor__body">
            <div class="page-editor" v-show="!advancedMode">
                <content-builder-form-editor
                    ref="editor"
                    :key="`${selectedId}-${formRenderIndex}`"
                    :content="currentContent"
                    :schemaKey="schemaKey"
                    @ready="loading = false"
                    @change="onContentChange">
                </content-builder-form-editor>
            </div>
            <div v-show="advancedMode">
                <code-editor
                    :value="currentContentString"
                    :enable-character-limit="false"
                    language="json"
                    @change="onAdvancedEditorContentChange($event)"
                    rows="40">
                </code-editor>
            </div>
        </div>
        <div v-else-if="selectedId"
            class="p-4">
            <p class="text-muted"><strong>Error:</strong> Missing data for object: <strong>{{ selectedId }}</strong>.</p>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import ContentBuilderFormEditor from './ContentBuilderFormEditor.vue'
import { cloneDataObject, getCleanTitle } from '../helpers/builder'
import { uuid, localizeDate } from '../helpers/util'
import EventBus from '../helpers/event-bus'
import { watch } from 'vue'

export default {
    components: {
        ContentBuilderFormEditor
    },
    props: {
        aiEnabled: {
            type: [Boolean, Number],
            default: false
        },
     },
    data () {  
        return {
            formRenderIndex: 0,
            loading: this.currentDataObject ? true : false,
            loadingHistory: false
        }
    },
    computed: {
        ...mapState('builder', {
            outline: state => state.outline,
            selectedId: state => state.selectedId
        }),
        ...mapGetters('builder', [
            'currentDataObject',
            'currentInteraction',
            'currentObjectStatus',
            'schemaRefs',
            'interactions',
            'changes'
        ]),
        ...mapGetters([
            'isLiteEditor'
        ]),
        currentContent () {
            return _.get(this.currentDataObject, 'data')
        },
        currentContentString () {
            return JSON.stringify(this.currentContent, null, 2)
        },
        currentSchema () {
            return this.getSchema(this.currentDataObject.schema_ref)
        },
        currentHistory () {
            const original = _.find(this.$store.state.builder.dataObjectSource, { id: this.selectedId })
            if (original) {
                const fields = _.pick(original, ['updated_at', 'data', 'editor'])
                fields.data_object_id = original.id
                fields.version = original.revision
                const history = original.revisions || this.$store.state.builder.revisionHistory[this.selectedId] || []
                return _.concat([fields], history)
            }
            return []
        },
        schemaKey () {
            if (this.schemaRefs?.definitions[this.currentDataObject.schema_ref]) {
                return this.currentDataObject.schema_ref
            }
            return `screens/${this.currentDataObject.type}/schema.json`
        },
        interaction () {
            return _.find(this.interactions, this.currentInteraction)
        },
        isShared () {
            return this.currentDataObject.type === 'global'
        },
        isAdmin () {
            return this.$store.getters.isAdmin
        },
        canExportScript () {
            return this.currentDataObject && this.$store.state.builder.sourceType === 'project'
        },
        exportUrl () {
            const url = this.$store.getters['builder/sourceUrl']
            return `${url}/content/${this.currentDataObject.id}/script`
        },
        aiMode: {
            get () {
                return this.$store.state.builder.aiMode
            },
            set (value) {
                this.$store.commit('builder/setAiMode', value)
            }
        },
        advancedMode: {
            get () {
                return this.$store.state.builder.advancedMode
            },
            set (value) {
                this.$store.commit('builder/setAdvancedMode', value)
            }
        }
    },
    watch: {
        advancedMode (value) {
            if (!value) {
                this.formRenderIndex++
            }
        },
        currentDataObject (value, oldVal) {
            if (value?.id !== oldVal?.id) {
                this.loading = true 
            }
        }
    },
    methods: {
        getCleanTitle: getCleanTitle,
        localizeDate: localizeDate,
        getSchema (ref) {
            const schema = _.get(this.schemaRefs, ref, { })
            return _.cloneDeep(schema)
        },
        getDefaultName (page) {
            let title = _.get(page, 'title') || 'Untitled Screen 01'
            return _.kebabCase(title)
        },
        updatePageTitle (e) {
            const title = e.target.textContent || e.target.innerText
            if (title !== this.currentDataObject.title) {
                this.$store.commit('builder/updateDataObject', { id: this.currentDataObject.id, title })
            }
        },
        updatePageName (e) {
            const name = e.target.textContent || e.target.innerText || this.getDefaultName(this.currentDataObject)
            if (name !== this.currentDataObject.name) {
                this.$store.commit('builder/updateDataObject', { id: this.currentDataObject.id, name })
            }
        },
        duplicatePage (source) {
            // generate new uuid name
            const dataObject = { name: uuid(4, `${source.name}-`) }
            _.assign(dataObject, cloneDataObject(source, ['id', 'name']))
            this.$store.dispatch('builder/addPage', dataObject)
        },
        async updateHistory (id) {
            this.loadingHistory = true
            await this.$store.dispatch('builder/getRevisionHistory', id)
            this.loadingHistory = false
        },
        async selectRevision (revision) {
            if (this.currentObjectStatus === 'changed' && !confirm('Are you sure you want to replace your local changes with the selected revision?')) {
                return
            }

            const result = {
                id: revision.data_object_id,
                data: revision.data,
                revision: revision.version
            }

            if (!result.data) {
                this.loadingHistory = true
                const revisionData = await this.$store.dispatch('builder/getRevisionData', revision)
                result.data = revisionData.data
                this.loadingHistory = false
            }

            this.$store.commit('builder/updateDataObject', result)

            // force re-render
            this.formRenderIndex++
        },
        removePage (source) {
            this.$store.dispatch('builder/removePage', source)
        },
        restorePage ({ id }) {
            this.$store.commit('builder/removeRemoval', id)
        },
        onAdvancedEditorContentChange (event) {
            let data = event.target.value
            this.onContentChange(data);
        },
        onContentChange (data) {
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data)
                } catch (err) {
                    alert('JSON syntax invalid, please fix the error and try again.')
                    return
                }
            }

            this.$store.commit('builder/updateDataObject', {
                ...this.currentDataObject,
                data: _.cloneDeep(data)
            })
        },
        toggleAi () {
            const toggle = !this.aiMode
            this.$store.commit('builder/setAiMode', toggle)
        },
        toggleMode () {
            const toggle = !this.advancedMode
            this.$store.commit('builder/setAdvancedMode', toggle)
        }
    },
    mounted () {
        EventBus.$on('force:render', () => { this.formRenderIndex++ })
    }
}
</script>
